import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  UserCircle,
  Building2,
  Scale,
  Landmark,
  Filter,
  X,
  Tag,
  Trash2,
  CheckCircle2,
  SearchX,
} from "lucide-react";
import { contactsApi, PaginatedResponse } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import { notifyError } from "../ui/Toast";

interface Contact {
  _id: string;
  displayName: string;
  category: string;
  emails: { label: string; email: string }[];
  phones: { label: string; number: string }[];
  tags: string[];
  createdAt: string;
}

const categoryOptions = [
  { value: "", label: "All Categories" },
  { value: "person", label: "Person" },
  { value: "organization", label: "Organization" },
  { value: "lender", label: "Lender" },
  { value: "realtor", label: "Realtor" },
  { value: "opposingCounsel", label: "Opposing Counsel" },
  { value: "court", label: "Court" },
  { value: "vendor", label: "Vendor" },
  { value: "other", label: "Other" },
];

const categoryBadge: Record<string, { className: string; Icon: React.FC<any> }> = {
  person: { className: "bg-blue-100 text-blue-700", Icon: UserCircle },
  organization: { className: "bg-purple-100 text-purple-700", Icon: Building2 },
  lender: { className: "bg-cyan-100 text-cyan-700", Icon: Landmark },
  realtor: { className: "bg-pink-100 text-pink-700", Icon: Building2 },
  opposingCounsel: { className: "bg-red-100 text-red-700", Icon: Scale },
  court: { className: "bg-indigo-100 text-indigo-700", Icon: Landmark },
  vendor: { className: "bg-amber-100 text-amber-700", Icon: Building2 },
  other: { className: "bg-slate-100 text-slate-600", Icon: UserCircle },
};

const formatCategory = (cat: string) =>
  cat === "opposingCounsel"
    ? "Opposing Counsel"
    : cat.charAt(0).toUpperCase() + cat.slice(1);

const ContactsPage: React.FC = () => {
  // Routed at /contacts/:id from the People list; open that contact when present
  const { id: routeContactId } = useParams<{ id: string }>();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;

      const res = await contactsApi.list(params);
      const data = res.data as PaginatedResponse<Contact>;
      setContacts(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, categoryFilter]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Form State ────
  const [formData, setFormData] = useState({
    displayName: "",
    category: "person",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setEditingContact(null);
    setFormData({ displayName: "", category: "person", firstName: "", lastName: "", email: "", phone: "", notes: "" });
    setShowModal(true);
  };

  const openEdit = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      displayName: contact.displayName,
      category: contact.category,
      firstName: "",
      lastName: "",
      email: contact.emails?.[0]?.email || "",
      phone: contact.phones?.[0]?.number || "",
      notes: "",
    });
    setShowModal(true);
  };

  // Open the contact named in the URL
  useEffect(() => {
    if (!routeContactId) return;
    let cancelled = false;
    contactsApi
      .get(routeContactId)
      .then((res) => {
        if (!cancelled && res.data) openEdit(res.data as Contact);
      })
      .catch((err) => {
        if (!cancelled) notifyError("Couldn't open that contact. It may have been deleted.", err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeContactId]);

  const closeModal = () => {
    setShowModal(false);
  };

  const handleDelete = async () => {
    if (!editingContact) return;
    if (!window.confirm(`Delete "${editingContact.displayName}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await contactsApi.remove(editingContact._id);
      closeModal();
      fetchContacts();
    } catch (err) {
      console.error("Failed to delete contact:", err);
      notifyError("Couldn't delete the contact.", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        displayName: formData.displayName,
        category: formData.category,
        emails: formData.email ? [{ label: "main", email: formData.email }] : [],
        phones: formData.phone ? [{ label: "main", number: formData.phone }] : [],
        notes: formData.notes || null,
      };

      if (formData.category === "person") {
        payload.person = { firstName: formData.firstName, lastName: formData.lastName };
      }

      if (editingContact) {
        await contactsApi.update(editingContact._id, payload);
      } else {
        await contactsApi.create(payload);
      }

      closeModal();
      fetchContacts();
    } catch (err) {
      console.error("Failed to save contact:", err);
      notifyError("Couldn't save the contact.", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Contacts</h1>
          <p className="text-sm text-slate-500 mt-1">
            {pagination.total} contact{pagination.total !== 1 ? "s" : ""} in your directory
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-5 w-5" />
          New Contact
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div className="w-56">
            <SearchableSelect
              options={categoryOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={categoryFilter}
              onChange={(val) => {
                setCategoryFilter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Categories"
              icon={Filter}
              clearable
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : contacts.length === 0 ? (
          search || categoryFilter ? (
            /* No search / filter results */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-5 rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm">
                  <SearchX className="h-10 w-10 text-slate-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">No results found</h3>
              <p className="text-sm text-slate-500 text-center max-w-md leading-relaxed">
                No contacts match your current search or filters. Try adjusting your criteria or clear your filters to see all contacts.
              </p>
            </motion.div>
          ) : (
            /* True empty state — no contacts at all */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <UserCircle className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">No contacts in your directory</h3>
              <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                Build your professional network — add opposing counsel, vendors, courts,
                and other contacts you work with regularly.
              </p>
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                <Plus className="h-5 w-5" />
                Add Your First Contact
              </button>
              <div className="mt-8 flex flex-wrap justify-center gap-4 max-w-lg">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Opposing counsel & courts
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Vendors & lenders
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Realtors & experts
                </div>
              </div>
            </motion.div>
          )
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Category
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Email
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                      Phone
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">
                      Tags
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact, i) => {
                    const cat = categoryBadge[contact.category] || categoryBadge.other;
                    const CatIcon = cat.Icon;
                    return (
                      <motion.tr
                        key={contact._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        onClick={() => openEdit(contact)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full flex items-center justify-center bg-slate-100 text-slate-500 flex-shrink-0">
                              <UserCircle className="h-5 w-5" />
                            </div>
                            <span className="font-medium text-sm text-slate-800">
                              {contact.displayName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cat.className}`}
                          >
                            <CatIcon className="h-3 w-3" />
                            {formatCategory(contact.category)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                          {contact.emails?.[0]?.email || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                          {contact.phones?.[0]?.number || "-"}
                        </td>
                        <td className="px-6 py-4 hidden xl:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(contact.tags || []).slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs"
                              >
                                <Tag className="h-3 w-3" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      <PremiumModal
        open={showModal}
        onClose={closeModal}
        title={editingContact ? "Edit Contact" : "New Contact"}
        subtitle={editingContact ? "Update contact details" : "Add a new contact to your directory"}
        icon={UserCircle}
        footer={
          <div className="flex items-center justify-between">
            {editingContact ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="contact-form"
                disabled={saving || !formData.displayName}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                {saving ? "Saving..." : editingContact ? "Save Changes" : "Create Contact"}
              </button>
            </div>
          </div>
        }
      >
        <form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Display Name *</label>
            <input
              type="text"
              required
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Jane Doe or ABC Lending"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <SearchableSelect
              options={categoryOptions.filter((o) => o.value).map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={formData.category}
              onChange={(val) => setFormData({ ...formData, category: val || "person" })}
              placeholder="Select category"
            />
          </div>

          {formData.category === "person" && !editingContact && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="contact@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
            />
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default ContactsPage;
